import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Archive, Plus, Search, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HelpHint } from '../components/HelpHint';
import { HoverCard } from '../components/Hover';
import { SlaBadge } from '../components/SlaBadge';
import { PriorityBadge, StatusBadge } from '../components/StatusBadge';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { DataTable, type Column } from '../components/DataTable';
import { SelectionBar } from '../components/SelectionBar';
import { useToast } from '../components/Toast';
import type { TicketSummary } from '../lib/types';

const OPEN_STATUSES = ['NEW', 'TRIAGED', 'DISPATCHING', 'ASSIGNED', 'EN_ROUTE', 'ON_SITE', 'IN_PROGRESS'];

export function TicketsPage() {
  const { can } = useSession();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const tickets = useQuery({
    queryKey: ['tickets', status, query, showArchived, unassignedOnly, page],
    queryFn: () =>
      api.tickets({
        status: status || undefined,
        q: query || undefined,
        page,
        ...(showArchived ? { archived: 'true' } : {}),
        ...(unassignedOnly ? { unassigned: 'true' } : {}),
      }),
    refetchInterval: 10_000,
    placeholderData: (previous) => previous,
  });

  const stats = useQuery({ queryKey: ['ticket-stats'], queryFn: api.ticketStats });

  const rows = tickets.data?.tickets ?? [];
  const open = rows.filter((t) => OPEN_STATUSES.includes(t.status)).length;

  /* Most urgent first when sorted ascending — see BoardPage for why a rank. */
  const URGENCY: Record<string, number> = { P1: 1, P2: 2, P3: 3, P4: 4 };

  const columns: Column<TicketSummary>[] = [
    {
      key: 'reference',
      header: 'Ref',
      sortBy: (ticket) => ticket.reference,
      cell: (ticket) => (
        /*
          Who it is for and when it came in — the two facts this table has no
          column for, and the two people ask about most. Resting on the
          reference shows them; opening the ticket shows the same in full.
        */
        <HoverCard
          title={ticket.reference}
          facts={[
            { label: 'Customer', value: ticket.customerName ?? 'Not recorded' },
            { label: 'Raised', value: new Date(ticket.createdAt).toLocaleString() },
            { label: 'Job type', value: ticket.jobTypeName },
          ]}
          note="Open to see the full history."
        >
          <span className="font-medium text-primary-600">{ticket.reference}</span>
        </HoverCard>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      sortBy: (ticket) => ticket.title.toLowerCase(),
      cell: (ticket) => (
        <div className="min-w-0">
          <span>{ticket.title}</span>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{ticket.jobTypeName}</span>
            {ticket.isIncident && (
              <span className="badge bg-danger-50 text-danger-600">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                incident
              </span>
            )}
            {ticket.childCount > 0 && (
              <span className="badge bg-muted text-muted-foreground">
                <Users className="h-3 w-3" aria-hidden="true" />
                {ticket.childCount} linked
              </span>
            )}
            <SlaBadge
              state={ticket.slaState}
              percent={ticket.slaPercent}
              targetMin={ticket.slaTargetMin}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'area',
      header: 'Area',
      secondary: true,
      sortBy: (ticket) => ticket.areaName ?? null,
      cell: (ticket) => (
        <span className="whitespace-nowrap">
          {ticket.areaName ?? '—'}
          {ticket.zoneCode && (
            <span className="ml-1.5 text-xs text-muted-foreground">{ticket.zoneCode}</span>
          )}
        </span>
      ),
    },
    {
      key: 'technician',
      header: (
        <span className="inline-flex items-center gap-1.5">
          Technician
          <HelpHint>
            The number shown is where the ticket was sent. If it looks wrong, fix it on the
            technician&apos;s profile before assigning again.
          </HelpHint>
        </span>
      ),
      /* Unassigned sinks rather than sorting under U — the bucket filter is
         for finding those; this column is for finding a person. */
      sortBy: (ticket) => ticket.assignee?.name ?? null,
      cell: (ticket) =>
        ticket.assignee ? (
          <span className="whitespace-nowrap">
            {ticket.assignee.name}
            <span className="block text-xs text-muted-foreground">
              {ticket.assignee.phoneE164}
            </span>
          </span>
        ) : can('ticket:assign') ? (
          /*
            The row said "Unassigned" and stopped there, so the only way to act
            on it was to know that opening the ticket reveals an Assign card
            further down the right-hand column. People who did not already know
            that reported there was nowhere to assign from — which was fair:
            a label naming a problem, beside no way to fix it.

            Goes straight to the panel rather than the top of the ticket, so
            the click lands on the thing it promised.
          */
          <Link
            to={`/tickets/${ticket.id}#assign`}
            className="whitespace-nowrap underline underline-offset-2 hover:text-foreground"
            onClick={(event) => event.stopPropagation()}
          >
            Assign
          </Link>
        ) : (
          <span className="text-muted-foreground">Unassigned</span>
        ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortBy: (ticket) => URGENCY[ticket.priority] ?? 99,
      cell: (ticket) => <PriorityBadge priority={ticket.priority} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortBy: (ticket) => ticket.status,
      cell: (ticket) => (
        <>
          <StatusBadge status={ticket.status} />
          {ticket.onHoldReason && (
            <span className="mt-0.5 block max-w-40 truncate text-xs text-muted-foreground">
              {ticket.onHoldReason}
            </span>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">Tickets</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {open} open ·{' '}
            {/*
              The unassigned count is the one figure here somebody can act on,
              so it is the one that does something. It was plain text: it said
              how many tickets nobody was going to do, and left finding them as
              an exercise.

              A toggle rather than a link, because the honest opposite of
              "show me those" is "show me everything again", and a filter you
              cannot see you are inside is how people conclude their tickets
              have vanished.
            */}
            <button
              type="button"
              onClick={() => {
                setUnassignedOnly((on) => !on);
                setPage(1);
              }}
              aria-pressed={unassignedOnly}
              className={
                unassignedOnly
                  ? 'rounded px-1.5 py-0.5 font-medium text-foreground underline underline-offset-2'
                  : 'rounded px-1.5 py-0.5 underline underline-offset-2 hover:text-foreground'
              }
            >
              {stats.data?.unassigned ?? 0} unassigned
            </button>{' '}
            · {stats.data?.breached ?? 0} SLA breached
            {unassignedOnly && (
              <span className="ml-2 text-xs">
                showing only unassigned —{' '}
                <button
                  type="button"
                  onClick={() => {
                    setUnassignedOnly(false);
                    setPage(1);
                  }}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  show all
                </button>
              </span>
            )}
          </p>
        </div>
        {can('ticket:create') && (
          <Link to="/tickets/new" className="btn-primary">
            <Plus className="h-4 w-4" aria-hidden="true" />
            New ticket
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          {/*
            * `type="search"` so it looks and behaves like every other list's
            * search box — including the clear affordance browsers put in one.
            *
            * This searches on the *server*, unlike the in-memory drawer on the
            * unpaged lists, and that is the right way round: this list is paged,
            * so filtering in the browser would narrow the page somebody is
            * looking at and quietly leave the rest of the matches on page four.
            */}
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reference or title…"
            aria-label="Search tickets"
            className="input-field pl-9"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter by status"
          className="input-field w-auto"
        >
          <option value="">All statuses</option>
          {[...OPEN_STATUSES, 'ON_HOLD', 'PENDING_CUSTOMER', 'WORK_DONE', 'CLOSED', 'CANCELLED'].map(
            (s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, ' ').toLowerCase()}
              </option>
            ),
          )}
        </select>

        {/*
          Archiving is only safe to use if there is somewhere to look afterwards.
          Without this the button felt like a delete, so nobody pressed it and
          duplicates stayed on the board.
        */}
        {can('ticket:archive') && (
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            aria-pressed={showArchived}
            className={`input-field w-auto ${
              showArchived ? 'border-primary-500 bg-primary-50 text-primary-800' : ''
            }`}
          >
            <Archive className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
            {showArchived ? 'Viewing archive' : 'Archive'}
          </button>
        )}
      </div>

      {showArchived && (
        <p className="rounded-xl bg-muted p-3 text-sm text-muted-foreground">
          Archived tickets. They keep their full history and SLA figures, and can be restored from
          the ticket itself.
        </p>
      )}

      {/*
        The same table every other list uses.
        -------------------------------------
        This was a hand-built one, which is why it was the only list in the
        application with no checkboxes, no sortable headings and its own
        loading and empty rows. Everything below the surface — the pager, the
        sorting, the selection — now comes from the shared component, so the
        next thing added to it arrives here without anybody remembering to.

        What was worth keeping is kept: the hover card on the reference, the
        SLA edge colour, and the sub-line under the title.
      */}
      <SelectionBar
        rows={rows}
        selected={selected}
        onClear={() => setSelected(new Set())}
        rowKey={(ticket) => ticket.id}
        exportName={showArchived ? 'archived-tickets' : 'tickets'}
        csv={[
          { header: 'Reference', value: (t) => t.reference },
          { header: 'Title', value: (t) => t.title },
          { header: 'Job type', value: (t) => t.jobTypeName },
          { header: 'Status', value: (t) => t.status },
          { header: 'Priority', value: (t) => t.priority },
          { header: 'Area', value: (t) => t.areaName },
          { header: 'Zone', value: (t) => t.zoneCode },
          { header: 'Customer', value: (t) => t.customerName },
          { header: 'Assigned to', value: (t) => t.assignee?.name ?? '' },
          { header: 'Assignee phone', value: (t) => t.assignee?.phoneE164 ?? '' },
          { header: 'Source', value: (t) => t.source },
          { header: 'Website form', value: (t) => t.sourceForm ?? '' },
          { header: 'SLA breached', value: (t) => (t.slaBreached ? 'yes' : 'no') },
          { header: 'On hold because', value: (t) => t.onHoldReason ?? '' },
          { header: 'Raised', value: (t) => t.createdAt },
        ]}
        actions={
          can('ticket:archive') && !showArchived
            ? [
                {
                  key: 'archive',
                  label: 'Archive these',
                  icon: Archive,
                  destructive: true,
                  confirm: (chosen) =>
                    `Archive ${chosen.length} ticket${chosen.length === 1 ? '' : 's'}?\n\n` +
                    'They keep their history and can be restored from the ticket itself.',
                  run: async (chosen) => {
                    /*
                     * One at a time and counted. Each archive is audited and
                     * each can legitimately refuse, so "forty requests, two
                     * failed" is not "done" — the count is what gets reported.
                     */
                    let done = 0;
                    const failed: string[] = [];
                    for (const ticket of chosen) {
                      try {
                        await api.archiveTicket(ticket.id, 'Archived in bulk from All tickets');
                        done += 1;
                      } catch {
                        failed.push(ticket.reference);
                      }
                    }

                    setSelected(new Set());
                    await queryClient.invalidateQueries({ queryKey: ['tickets'] });
                    await queryClient.invalidateQueries({ queryKey: ['ticket-stats'] });

                    if (failed.length === 0) toast.done(`${done} archived.`);
                    else
                      toast.failed(
                        `${done} archived. ${failed.length} refused: ` +
                          failed.slice(0, 3).join(', ') +
                          (failed.length > 3 ? '…' : ''),
                      );
                  },
                },
              ]
            : []
        }
      />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(ticket) => ticket.id}
        selected={selected}
        onSelect={setSelected}
        onRowClick={(ticket) => navigate(`/tickets/${ticket.id}`)}
        page={
          tickets.data
            ? {
                page: tickets.data.page,
                perPage: tickets.data.perPage,
                total: tickets.data.total,
                pages: tickets.data.pages,
              }
            : undefined
        }
        onPage={setPage}
        caption="Every ticket, newest first"
        empty={
          <p className="rounded-2xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            {tickets.isPending
              ? 'Loading…'
              : 'No tickets match that filter — clear the search, or create one.'}
          </p>
        }
      />

    </div>
  );
}
